import { MESSAGE, RealtimeClient, createPublishSaga, createSubscribeSaga } from "@classdojo/web/pods/realtime";
import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import { SagaIterator } from "redux-saga";
import { take, fork } from "redux-saga/effects";
import getRealtimeAdapter from "./adapter";
import {
  fetchHostState,
  displayOpen,
  displayClose,
  createPairingSaga,
  pairedHandler,
  DISPLAY_OPEN,
  DISPLAY_CLOSE,
  displayOpenHandler,
  displayCloseHandler,
  RECEIVE_DISPLAY_OPEN,
  RECEIVE_DISPLAY_CLOSE,
  RECEIVE_REMOTE_CLOSE,
  displayNavSaga,
  overWireSaga,
  REMOTE_CLOSE,
  remoteClose,
  remoteNavSaga,
  SHARE_OVER_WIRE_RECEIVE,
  SHARE_OVER_WIRE_SEND,
  shareOverWire,
} from "./data/pairing";
import { role, isRemote, isDisplay } from "./util/role";
import { FETCH_SESSION_DONE, UserConfig } from "pods/auth";
import { SEND_GROUPS, RECEIVE_GROUPS } from "src/pods/groups";
import { SEND_IDEAS, RECEIVE_IDEAS } from "src/pods/ideas";
import {
  SEND_NOISE,
  RECEIVE_NOISE,
  SEND_MICROPHONE_PERMISSION_ERROR,
  RECEIVE_MICROPHONE_PERMISSION_ERROR,
} from "src/pods/noise";
import { SEND_RADIO, RECEIVE_RADIO } from "src/pods/radio";
import { AppMetadata } from "src/pods/toolkitData/components/EditableSetup";

export { fetchHostState, displayOpen, displayClose, remoteClose, shareOverWire };

declare global {
  interface Window {
    rtcClient: RealtimeClient;
  }
}

const STATE_KEY = "realtime";

export type RealtimeState = {
  [STATE_KEY]: RealtimeInitialState;
};

type RealtimeInitialState = {
  paired: boolean;
  apps: AppMetadata[];
  displayOn: boolean;
};

export const initialState: RealtimeInitialState = {
  paired: false,
  apps: [],
  displayOn: false,
};

const reducer = combineActionHandlers(initialState, [pairedHandler, displayOpenHandler, displayCloseHandler]);

const install: PodInstallFunction = (installReducer, installSaga) => {
  installReducer(STATE_KEY, reducer);
  installSaga(realtimeSaga);
};

export default install;

export const selectPaired = (state: RealtimeState) => state?.[STATE_KEY]?.paired || false;
export const selectDisplayOn = (state: RealtimeState) => state?.[STATE_KEY]?.displayOn || false;
export const selectState = (state: RealtimeState) => state?.[STATE_KEY];

function* realtimeSaga(): SagaIterator<UserConfig | { error: string } | undefined> {
  if (!isRemote && !isDisplay) {
    return;
  }
  // do this regardless whether we have session or not
  if (isRemote) yield fork(remoteNavSaga);
  if (isDisplay) yield fork(displayNavSaga);

  yield fork(overWireSaga);

  let channel: string | undefined = undefined;
  let userId: string | undefined = undefined;
  // The session must have produced a userId and mqttChannel to proceed.
  while (!channel || !userId) {
    const { error, payload } = yield take([FETCH_SESSION_DONE]);
    if (error) {
      continue;
    }

    const data = payload.response;
    if (data.type !== "teacher") {
      continue;
    }

    channel = data.userConfig.mqttChannels.self;
    userId = data.teacher._id;
  }

  const adapter = getRealtimeAdapter(userId);
  const client = new RealtimeClient(role!, channel, adapter);
  if (Config.cypressTest) {
    window.rtcClient = client;
  }

  yield fork(createPublishSaga(client, mapFromAction));
  yield fork(createSubscribeSaga(client, mapToAction));
  yield fork(createPairingSaga(client));
}

// responsible for taking realtime messages and translating them to Redux actions
// exported only for test
export function mapToAction({ type, payload }: { type: string; payload?: Record<string, unknown> }) {
  switch (type) {
    // app messages
    case MESSAGE.SEND_GROUPS:
      return { type: RECEIVE_GROUPS, payload };
    case MESSAGE.SEND_RADIO:
      return { type: RECEIVE_RADIO, payload };
    case MESSAGE.SEND_NOISE:
      return { type: RECEIVE_NOISE, payload };
    case MESSAGE.SEND_MICROPHONE_PERMISSION_ERROR:
      return { type: RECEIVE_MICROPHONE_PERMISSION_ERROR, payload };
    case MESSAGE.SEND_IDEAS:
      return { type: RECEIVE_IDEAS, payload };

    // special messages
    case MESSAGE.DISPLAY_OPEN:
      return { type: RECEIVE_DISPLAY_OPEN, payload };
    case MESSAGE.SHARE_OVER_WIRE_SEND:
      return { type: SHARE_OVER_WIRE_RECEIVE, payload };
    case MESSAGE.DISPLAY_CLOSE:
      return { type: RECEIVE_DISPLAY_CLOSE };
    case MESSAGE.REMOTE_CLOSE:
      return { type: RECEIVE_REMOTE_CLOSE };

    default:
      return null;
  }
}

// responsible for taking Redux actions and translating them to realtime messages
// exported only for test
export function mapFromAction({ type, payload }: { type: string; payload?: Record<string, unknown> }) {
  switch (type) {
    // app actions
    case SEND_GROUPS:
      return { type: MESSAGE.SEND_GROUPS, payload };
    case SEND_RADIO:
      return { type: MESSAGE.SEND_RADIO, payload };
    case SEND_NOISE:
      return { type: MESSAGE.SEND_NOISE, payload };
    case SEND_MICROPHONE_PERMISSION_ERROR:
      return { type: MESSAGE.SEND_MICROPHONE_PERMISSION_ERROR, payload };
    case SEND_IDEAS:
      return { type: MESSAGE.SEND_IDEAS, payload };

    // special actions
    case DISPLAY_OPEN:
      return { type: MESSAGE.DISPLAY_OPEN, payload };
    case SHARE_OVER_WIRE_SEND:
      return { type: MESSAGE.SHARE_OVER_WIRE_SEND, payload };
    case DISPLAY_CLOSE:
      return { type: MESSAGE.DISPLAY_CLOSE };
    case REMOTE_CLOSE:
      return { type: MESSAGE.REMOTE_CLOSE };

    default:
      return null;
  }
}
